import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { TrendingUp, Building2, Globe, Users, DollarSign, Target } from 'lucide-react';

export const Policy = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1B4F72] to-[#138808] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Why This Matters Now</h1>
          <p className="text-xl text-gray-100 max-w-4xl mx-auto leading-relaxed">
            India is at a pivotal moment in its mobility journey. Policy momentum, market demand, and technological readiness have aligned — creating a once-in-a-generation opportunity for a unified mobility layer.
          </p>
        </div>
      </section>

      {/* Policy Momentum */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-12">
            <div className="text-5xl">🏛️</div>
            <div>
              <h2 className="text-4xl font-bold text-[#1B4F72]">Policy Momentum</h2>
              <p className="text-gray-600 text-lg">National and state governments are actively pushing for integrated, data-driven mobility systems</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF9933] to-[#E68A2E] rounded-xl flex items-center justify-center mb-6">
                  <Building2 className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">National Urban Mobility Mission</h3>
                <p className="text-gray-700 mb-4">India's mobility policy framework emphasizes multimodal integration, public transport priority, and technology-enabled planning.</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Interoperable MaaS platforms
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Open data and APIs
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Reduced congestion and emissions
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1B4F72] to-[#2E86AB] rounded-xl flex items-center justify-center mb-6">
                  <Target className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">State-Level Mandates</h3>
                <p className="text-gray-700 mb-4">State governments and city authorities are increasingly seeking unified dashboards and real-time operational visibility.</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    RTC modernization initiatives
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Smart city mobility programs
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Outcome-based transport funding
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#138808] to-[#0F6806] rounded-xl flex items-center justify-center mb-6">
                  <Globe className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">Digital Public Infrastructure</h3>
                <p className="text-gray-700 mb-4">India's DPI success (UPI, Aadhaar, ONDC) demonstrates the power of neutral, interoperable platforms at national scale.</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Platform neutrality
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Ecosystem-driven adoption
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Public–private collaboration
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-12">
            <div className="text-5xl">📊</div>
            <div>
              <h2 className="text-4xl font-bold text-[#1B4F72]">Market Opportunity</h2>
              <p className="text-gray-600 text-lg">India's mobility market is vast, fragmented, and ready for integration</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-[#FF9933]/10 to-[#E68A2E]/10">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#FF9933] mb-2">₹1,00,000+ Cr</div>
                <p className="text-gray-700 font-semibold">Total annual mobility market size</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1B4F72]/10 to-[#2E86AB]/10">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#1B4F72] mb-2">8M+</div>
                <p className="text-gray-700 font-semibold">Vehicles added every year</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-gradient-to-br from-[#138808]/10 to-[#0F6806]/10">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#138808] mb-2">Billions</div>
                <p className="text-gray-700 font-semibold">Daily public transport journeys</p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <p className="text-xl text-gray-700 text-center leading-relaxed">
                Despite this scale, India lacks a unified digital layer that connects public, semi-public and private transport into a single, real-time system.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Global Proof Points */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-12">
            <div className="text-5xl">🌍</div>
            <div>
              <h2 className="text-4xl font-bold text-[#1B4F72]">Global Proof Points</h2>
              <p className="text-gray-600 text-lg">Integrated mobility platforms are already working — at city and national scale — across the world</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">Helsinki</h3>
                <p className="text-gray-700 leading-relaxed">
                  MaaS Global demonstrated that multi-modal journey planning and payments can significantly reduce private car usage.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">Singapore</h3>
                <p className="text-gray-700 leading-relaxed">
                  Government-led integration of buses, metro, and taxis created one of the world's most efficient urban mobility systems.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">Tokyo & Shanghai</h3>
                <p className="text-gray-700 leading-relaxed">
                  High-frequency, data-driven transport networks operate at extreme scale through centralized coordination and real-time systems.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="border-0 shadow-xl bg-gradient-to-r from-[#1B4F72]/5 to-[#138808]/5">
              <CardContent className="p-8">
                <p className="text-xl text-gray-700 font-semibold">
                  Mahasarathi adapts these proven models to India's unique complexity, diversity, and scale.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Clear Demand Signals */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-12">
            <div className="text-5xl">📣</div>
            <div>
              <h2 className="text-4xl font-bold text-[#1B4F72]">Clear Demand Signals</h2>
              <p className="text-gray-600 text-lg">The ecosystem is ready — and actively asking for this layer</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF9933] to-[#E68A2E] rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Users className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4 text-center">Citizen Demand</h3>
                <p className="text-gray-700 mb-4">Research across major Indian cities shows overwhelming demand for reliable, multi-modal journey planning.</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Reduced waiting and uncertainty
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Transparent fares
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Predictable commutes
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1B4F72] to-[#2E86AB] rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Building2 className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4 text-center">Operator Readiness</h3>
                <p className="text-gray-700 mb-4">RTCs and private operators are seeking tools to improve utilization and reduce losses.</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Digitization without disruption
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Better planning insights
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Fair integration models
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#138808] to-[#0F6806] rounded-full flex items-center justify-center mb-6 mx-auto">
                  <TrendingUp className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4 text-center">Investor Appetite</h3>
                <p className="text-gray-700 mb-4">Significant capital is flowing into mobility, infra-tech and GovTech — with growing focus on integration layers.</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Long-term infrastructure plays
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Platform economics
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Policy-aligned growth
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#1B4F72] to-[#138808] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">A Once-in-a-Generation Opportunity</h2>
          <p className="text-xl text-gray-100 mb-4 leading-relaxed">
            Just as UPI transformed digital payments in India, a neutral, interoperable mobility layer can redefine how the nation moves.
          </p>
          <p className="text-xl text-gray-100 leading-relaxed">
            Mahasarathi is being built to serve not just today's commuters, but India's mobility needs for decades to come.
          </p>
        </div>
      </section>
    </div>
  );
};
